<template>
  <v-container fluid>
    <v-card v-if="record" variant="outlined">
      <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4">
        <span>Production Requisition {{ record.code }}</span>
        <v-spacer />
        <v-chip :color="approvalColor(record.approval_status)" size="small">{{ record.approval_status || (record.status ? 'Pending' : 'Draft') }}</v-chip>
        <v-chip color="indigo" variant="tonal" size="small">{{ labelize(record.workflow_status) }}</v-chip>
        <v-btn class="d-print-none" icon="mdi-printer" variant="text" @click="printPage" />
      </v-card-title>
      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="6" md="3"><Info label="Requisition Date" :value="dateOnly(record.requisition_date)" /></v-col>
          <v-col cols="6" md="3"><Info label="Type" :value="labelize(record.type)" /></v-col>
          <v-col cols="6" md="3"><Info label="Sales Order" :value="record.sale_order?.code || '-'" /></v-col>
          <v-col cols="6" md="3"><Info label="Material Issues" :value="String((record.deliveries || []).length)" /></v-col>
          <v-col cols="12"><Info label="Remarks" :value="record.remarks || '-'" /></v-col>
        </v-row>

        <v-expansion-panels v-model="openPanels" multiple class="mt-4">
          <v-expansion-panel v-for="detail in record.details || []" :key="detail.id" :value="detail.id">
            <v-expansion-panel-title>
              <div class="d-flex flex-wrap align-center ga-2 w-100 pr-3">
                <strong>{{ detail.product?.name }}</strong>
                <v-chip size="x-small" color="primary" variant="tonal">{{ detail.template?.name }} v{{ detail.template_version_no || 1 }}</v-chip>
                <v-spacer />
                <span class="text-caption">Planned output: <strong>{{ number(detail.quantity) }}</strong> {{ detail.product?.unit?.name }}</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-alert type="info" density="compact" variant="tonal" class="mb-2">
                Template batch {{ number(detail.default_batch_quantity || 25) }} scaled to planned output {{ number(detail.quantity) }}.
              </v-alert>
              <v-table density="compact" class="border-sm">
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Type</th>
                    <th>Method</th>
                    <th class="text-center">Std. Purity</th>
                    <th class="text-center">Pr. Purity</th>
                    <th class="text-end">Formula Qty</th>
                    <th class="text-end">Adjusted Base Qty</th>
                    <th class="text-end">Loss %</th>
                    <th class="text-end">Loss Qty</th>
                    <th class="text-end">Physical Required</th>
                    <th class="text-end">Active Required</th>
                    <th class="text-end">Issued Active</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="raw in (detail.raw_product_details || []).filter(row => row.item_type !== 'packaging')" :key="raw.id">
                    <td>{{ raw.raw_product?.code }} - {{ raw.raw_product?.name }}</td>
                    <td>{{ labelize(raw.item_type) }}</td>
                    <td>{{ labelize(raw.calculation_method) }}</td>
                    <td class="text-center">{{ raw.standard_purity ? `${number(raw.standard_purity, 4)}%` : '-' }}</td>
                    <td class="text-center">{{ raw.calculation_method !== 'balancer' ? `${number(raw.present_purity ?? 100, 4)}%` : '-' }}</td>
                    <td class="text-end">{{ raw.formula_quantity == null ? '-' : number(raw.formula_quantity, 6) }}</td>
                    <td class="text-end">{{ number(raw.required_quantity, 6) }}</td>
                    <td class="text-end">{{ number(raw.process_loss_percent, 4) }}%</td>
                    <td class="text-end">{{ number(raw.loss_quantity) }}</td>
                    <td class="text-end">{{ number(raw.total_required_quantity) }}</td>
                    <td class="text-end">{{ number(raw.active_required_quantity, 6) }}</td>
                    <td class="text-end">{{ number(raw.issued_active_quantity, 6) }}</td>
                  </tr>
                </tbody>
              </v-table>
              <div class="text-caption text-medium-emphasis mt-2">
                Purity-adjusted quantity = Formula Qty × Standard Purity ÷ Present Purity.
                The Balancer uses the remaining batch weight; packaging is excluded from this balance.
              </div>
              <div class="section-title mt-4">Packaging Materials</div>
              <v-table density="compact" class="border-sm">
                <thead><tr><th>Packaging Product</th><th class="text-end">Planned Qty</th><th class="text-end">Issued Qty</th></tr></thead>
                <tbody>
                  <tr v-for="item in (detail.raw_product_details || []).filter(row => row.item_type === 'packaging')" :key="item.id">
                    <td>{{ item.raw_product?.code }} - {{ item.raw_product?.name }}</td>
                    <td class="text-end">{{ number(item.total_required_quantity) }}</td>
                    <td class="text-end">{{ number(item.delivery_qty) }}</td>
                  </tr>
                  <tr v-if="!(detail.raw_product_details || []).some(row => row.item_type === 'packaging')">
                    <td colspan="3" class="text-center text-medium-emphasis py-3">No packaging materials.</td>
                  </tr>
                </tbody>
              </v-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="section-title mt-4">Inventory Material Issue Trace</div>
        <v-table density="compact" class="border-sm">
          <thead><tr><th>Issue Code</th><th>Date</th><th>Status</th><th>Lots</th><th class="text-end">Cost</th></tr></thead>
          <tbody>
            <tr v-for="delivery in record.deliveries || []" :key="delivery.id">
              <td>{{ delivery.code }}</td>
              <td>{{ dateOnly(delivery.requisition_delivery_date) }}</td>
              <td>{{ delivery.approval_status || (delivery.status ? 'Pending' : 'Draft') }}</td>
              <td>
                <span v-for="allocation in delivery.allocations || []" :key="allocation.id" class="me-2 text-caption">
                  {{ allocation.lot_no || allocation.batch_no }} ({{ number(allocation.quantity) }} @ {{ number(allocation.purchase_purity, 4) }}%)
                </span>
              </td>
              <td class="text-end">{{ number(delivery.amount, 2) }}</td>
            </tr>
            <tr v-if="!(record.deliveries || []).length"><td colspan="5" class="text-center text-medium-emphasis">No material issue yet.</td></tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>
    <v-skeleton-loader v-else type="article, table" />
  </v-container>
</template>

<script setup>
import { defineComponent, h, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const Info = defineComponent({
  props: { label: String, value: [String, Number] },
  setup(props) {
    return () => h("div", { class: "info-box" }, [h("div", { class: "info-label" }, props.label), h("div", { class: "font-weight-medium text-capitalize" }, props.value ?? "-")]);
  },
});
const route = useRoute();
const store = useStore();
const record = ref(null);
const openPanels = ref([]);

function number(value, digits = 4) { return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits }); }
function dateOnly(value) { return value ? String(value).slice(0, 10) : ""; }
function labelize(value) { return String(value || "-").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()); }
function approvalColor(status) { return status === "Approved" ? "success" : ["Cancelled", "Rejected"].includes(status) ? "error" : "warning"; }
function printPage() { window.print(); }

onMounted(async () => {
  try {
    const response = await store.dispatch("getRawMetarialRequisitionById", route.params.id);
    record.value = response?.data || response;
    openPanels.value = (record.value?.details || []).map((detail) => detail.id);
  } catch (error) {
    toast.error(error?.response?.data?.message || "Production requisition details could not be loaded.");
  }
});
</script>

<style scoped>
.info-box { border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px; height: 100%; padding: 8px 10px; }
.info-label { color: rgba(0, 0, 0, 0.6); font-size: 0.7rem; text-transform: uppercase; }
.section-title { background: #eceff1; font-size: 0.85rem; font-weight: 600; padding: 6px 8px; }
</style>
